import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SourceIcon from '@mui/icons-material/Source';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import Nav from './nav';

export const PAGE_BG = '#F5F8FC';
export const SLATE_TEXT = '#607086';
export const DARK_TEXT = '#10233B';
export const BRAND_BLUE = PRIMARYCOLOR || '#005CC5';
export const SUCCESS_GREEN = '#16A34A';
export const WARNING_AMBER = '#D97706';
export const FAIL_RED = '#C62828';

export const shellCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(BRAND_BLUE, 0.1),
  boxShadow: `0 14px 28px ${alpha(BRAND_BLUE, 0.06)}`,
  backgroundColor: '#ffffff',
};

export const formatNumber = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return String(value ?? 0);
  return numericValue.toLocaleString();
};

export const formatDate = (value) => {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

export const truncate = (value, max = 90) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

export const titleCase = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

export const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

export const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
  return String(value).trim() !== '';
};

export const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') return Object.values(value).flat();
  return [value];
};

export const pick = (object, keys, fallback = undefined) => {
  if (!object || typeof object !== 'object') return fallback;
  for (const key of keys) {
    if (hasValue(object[key])) return object[key];
  }
  return fallback;
};

export const unwrapPayload = (data, key) => {
  if (!data || typeof data !== 'object') return {};
  if (key && hasValue(data[key])) return data[key];
  if (hasValue(data.data)) return data.data;
  if (hasValue(data.payload)) return data.payload;
  if (hasValue(data.result)) return data.result;
  return data;
};

export const getInitials = (value) => {
  const parts = String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase()).join('') || 'OS';
};

export const sourceRefs = (item) => {
  const refs = pick(item, ['source_refs', 'sourceRefs', 'sources', 'refs', 'references'], []);
  return asArray(refs).filter(hasValue);
};

export const stringifyValue = (value) => {
  if (!hasValue(value)) return 'Unknown';
  if (Array.isArray(value)) return value.map(stringifyValue).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, entryValue]) => hasValue(entryValue))
      .map(([key, entryValue]) => `${titleCase(key)}: ${stringifyValue(entryValue)}`)
      .join(' | ');
  }
  return String(value);
};

export const valueText = (value, fallback = 'Unknown') => {
  if (!hasMeaningfulValue(value)) return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => valueText(item, '')).filter(Boolean).join(', ') || fallback;
  return stringifyValue(value) || fallback;
};

export const compactSourceLabel = (ref) => {
  if (!ref || typeof ref !== 'object') return truncate(String(ref || ''), 48);
  const provider = ref.provider || ref.source || ref.name || 'source';
  const path = ref.path || ref.url || ref.reference || '';
  return path ? `${provider}: ${path}` : String(provider);
};

export const countCollection = (value) => {
  if (!value) return 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object') return Object.values(value).reduce((sum, entry) => sum + countCollection(entry), 0);
  return 1;
};

export function useOsintFetch(path, pentestId, payloadKey) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    setError('');

    fetch(`${BASEURL}${path}?pentest_id=${pentestId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((responseData) => {
        if (!mounted) return;
        if (responseData?.error) {
          setError(responseData.message || 'Unable to load OSINT data.');
          setData(null);
          return;
        }
        setData(unwrapPayload(responseData, payloadKey));
      })
      .catch((fetchError) => {
        console.error(fetchError);
        if (mounted) {
          setError('Unable to load OSINT data.');
          setData(null);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [navigate, path, payloadKey, pentestId]);

  return { loading, error, data };
}

export const OsintShell = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: PAGE_BG }}>
    <Nav />
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>{children}</Box>
  </Box>
);

export const LoadingState = ({ label = 'Loading OSINT intelligence...' }) => (
  <OsintShell>
    <Box sx={{ minHeight: 420, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <CircularProgress />
      <Typography sx={{ color: SLATE_TEXT }}>{label}</Typography>
    </Box>
  </OsintShell>
);

export const ErrorState = ({ title = 'OSINT data unavailable', message }) => (
  <OsintShell>
    <Card sx={{ ...shellCardSx, maxWidth: 620, mx: 'auto', mt: 6 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.4} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(FAIL_RED, 0.1), color: FAIL_RED }}>
            <ErrorOutlineIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: DARK_TEXT }}>{title}</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: SLATE_TEXT, lineHeight: 1.8 }}>
              {message || 'The requested OSINT data could not be loaded right now.'}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  </OsintShell>
);

export const PageTitle = ({ eyebrow, title, description, chips = [] }) => (
  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
    <Box>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: BRAND_BLUE }}>
        {eyebrow}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.8, fontWeight: 900, color: DARK_TEXT }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" sx={{ mt: 0.95, maxWidth: 920, color: SLATE_TEXT, lineHeight: 1.8 }}>
          {description}
        </Typography>
      ) : null}
    </Box>

    {chips.length ? (
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {chips.filter(Boolean).map((chip) => (
          <Chip
            key={chip}
            label={chip}
            sx={{ fontWeight: 800, bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE }}
          />
        ))}
      </Stack>
    ) : null}
  </Box>
);

export const MetricCard = ({ label, value, helper, Icon, accent = BRAND_BLUE }) => (
  <Card sx={{ ...shellCardSx, height: '100%', borderTop: `3px solid ${accent}` }}>
    <CardContent sx={{ p: 2.2 }}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 3,
            bgcolor: alpha(accent, 0.12),
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.45, fontWeight: 900, color: DARK_TEXT, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.65 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export const SectionCard = ({ eyebrow, title, description, children, action, sx }) => (
  <Card sx={{ ...shellCardSx, ...sx }}>
    <CardContent sx={{ p: 2.5 }}>
      {(eyebrow || title || description || action) ? (
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: children ? 2 : 0 }}>
          <Box>
            {eyebrow ? (
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>
                {eyebrow}
              </Typography>
            ) : null}
            {title ? (
              <Typography variant="h6" sx={{ mt: 0.6, fontWeight: 900, color: DARK_TEXT }}>
                {title}
              </Typography>
            ) : null}
            {description ? (
              <Typography variant="body2" sx={{ mt: 0.7, color: SLATE_TEXT, lineHeight: 1.75 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
      ) : null}
      {children}
    </CardContent>
  </Card>
);

export const KeyValueGrid = ({ items }) => (
  <Grid container spacing={1.4}>
    {items.filter((item) => hasValue(item.value)).map((item) => (
      <Grid item xs={12} sm={6} lg={item.full ? 12 : 4} key={item.label}>
        <Paper elevation={0} sx={{ p: 1.6, height: '100%', borderRadius: 3, border: `1px solid ${alpha(BRAND_BLUE, 0.1)}`, bgcolor: alpha(BRAND_BLUE, 0.025) }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>
            {item.label}
          </Typography>
          <Typography sx={{ mt: 0.6, fontWeight: 800, color: DARK_TEXT, lineHeight: 1.55, wordBreak: 'break-word' }}>
            {stringifyValue(item.value)}
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

export const SourceRefs = ({ refs }) => {
  const visibleRefs = asArray(refs).filter(hasMeaningfulValue).slice(0, 8);
  if (!visibleRefs.length) return null;

  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
      {visibleRefs.map((ref, index) => (
        <Chip
          key={`${stringifyValue(ref)}-${index}`}
          size="small"
          icon={<SourceIcon fontSize="small" />}
          label={truncate(compactSourceLabel(ref), 48)}
          sx={{ bgcolor: alpha(BRAND_BLUE, 0.07), color: BRAND_BLUE, fontWeight: 700, '& .MuiChip-icon': { color: BRAND_BLUE } }}
        />
      ))}
    </Stack>
  );
};

export const EmptyState = ({ title = 'No data available', description = 'This section will populate once the OSINT agent returns matching intelligence.' }) => (
  <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha(BRAND_BLUE, 0.22)}`, bgcolor: alpha(BRAND_BLUE, 0.025) }}>
    <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>{title}</Typography>
    <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT, lineHeight: 1.7 }}>{description}</Typography>
  </Paper>
);

export const DataPills = ({ values, accent = BRAND_BLUE, limit = 12 }) => {
  const items = asArray(values).filter(hasMeaningfulValue).slice(0, limit);
  if (!items.length) return <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No values discovered.</Typography>;

  return (
    <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
      {items.map((value, index) => (
        <Chip
          key={`${stringifyValue(value)}-${index}`}
          label={truncate(valueText(value), 52)}
          sx={{ fontWeight: 800, bgcolor: alpha(accent, 0.08), color: accent, border: `1px solid ${alpha(accent, 0.14)}` }}
        />
      ))}
    </Stack>
  );
};

export const useNormalizedList = (data, keys) =>
  useMemo(() => {
    const direct = pick(data, keys, data);
    return asArray(direct).filter(hasValue);
  }, [data, keys]);
